import { useCallback, useEffect, useState } from 'react';
import { BaseEntityNameLess } from '@api/entities/BaseEntity';
import { SavePresetError } from '@api/presetsApi/types';
import { useEntityLoader } from '../../../entities/entity-form/model/useEntityLoader';
import { isErrorResponse, safelyParseJson } from '@utils/utils';
import { FormDataObj } from '@utils/types';
import { serviceApi } from '@api/serviceApi';
import { usePresetsContext } from '@context/presetsContext';

export type Props = {
    entityName: string;
};

export const useImportDistrictsLoader = <T extends BaseEntityNameLess>({ entityName }: Props) => {
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<({ errorDescription: string } & SavePresetError) | null>(
        null
    );
    const { selectedPresetId } = usePresetsContext();
    const { loading, entity, metadata, loadData, loadMetadata, changeForm } = useEntityLoader<T>({
        entityName,
    });

    useEffect(() => {
        return () => {
            setError(null);
        };
    }, []);

    const saveFn = useCallback(
        async (data: FormDataObj) => {
            setSaving(true);

            const abortController = new AbortController();
            const resp = await serviceApi.importDistricts(
                {
                    task: {
                        ...data,
                        presetId: selectedPresetId,
                    } as any,
                },
                abortController.signal
            );

            if (isErrorResponse(resp)) {
                const { error: _error, error_description } = resp;
                const err = safelyParseJson(_error as string) as SavePresetError;
                setError({
                    error: err.error,
                    details: err.details,
                    errorDescription: error_description,
                });
            }

            setSaving(false);

            return resp;
        },
        [selectedPresetId]
    );

    return {
        loading,
        saving,
        entity: (entity as T) ?? {},
        metadata,
        error,
        loadData,
        loadMetadata,
        changeForm,
        saveFn,
    };
};
